import React, { useCallback, useState } from 'react';

import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { bootstrapSecondary, grape, lightGrayBorder } from 'lib/css/colors';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  INTERNAL_TOOLS_GENERATE_MFR_SCORE_GAP_REPORT,
  SCHEMAS,
} from 'lib/networking/endpoints';
import ScoreGapReportType, { SCORE_GAP_REPORT_TYPE_LABELS } from 'lib/enums/ScoreGapReportType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const DEFAULT_SCORE_THRESHOLD = 80;
const MIN_SCORE_THRESHOLD = 0;
const MAX_SCORE_THRESHOLD = 100;

const INVALID_THRESHOLD_MSG =
  `Score threshold must be a number between ${MIN_SCORE_THRESHOLD} ` +
  `and ${MAX_SCORE_THRESHOLD}.`;

const MESSAGES = {
  SELECT_MFR: 'Please select a manufacturer.',
  SELECT_SCHEMA: 'Please select a schema for the customer-facing report.',
  INVALID_THRESHOLD: INVALID_THRESHOLD_MSG,
  WARNING_TITLE: 'Warning!',
  ERROR_TITLE: 'Error!',
};

const REPORT_TYPE_OPTIONS = Object.values(ScoreGapReportType).map(value => ({
  label: SCORE_GAP_REPORT_TYPE_LABELS[value],
  value,
}));

function ManufacturerScoreGapReport() {
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [scoreThreshold, setScoreThreshold] = useState(String(DEFAULT_SCORE_THRESHOLD));
  const [reportType, setReportType] = useState(ScoreGapReportType.INTERNAL);
  const [selectedSchema, setSelectedSchema] = useState(null);

  const isCustomerReport = reportType === ScoreGapReportType.CUSTOMER;

  const { data: { data: schemas = [] } = {}, loading: schemasLoading } = useGet(
    SCHEMAS,
    {},
    !isCustomerReport,
  );

  const { postData: queueReportPost, loading: queueing } = usePost(
    INTERNAL_TOOLS_GENERATE_MFR_SCORE_GAP_REPORT,
    res => {
      const result = res?.data?.data;
      if (result) {
        Swal.fire({
          title: 'Report Queued',
          html:
            `<p>${result.message}</p>` +
            '<p>The report generation task has been queued and will be processed shortly. ' +
            'Once complete, you can download the generated file from the ' +
            '<a href="/file-exports" target="_blank" rel="noopener noreferrer">' +
            'File Exports Page</a>.</p>',
          icon: 'success',
          confirmButtonText: 'OK',
        });
      }
    },
    error => {
      const errorMsg =
        error?.response?.data?.error || 'Failed to queue manufacturer score gap report.';
      Swal.fire({
        title: MESSAGES.ERROR_TITLE,
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
  );

  const handleGenerate = useCallback(() => {
    if (!selectedManufacturer) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.SELECT_MFR,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (isCustomerReport && !selectedSchema) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.SELECT_SCHEMA,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    const numericThreshold = Number(scoreThreshold);
    const isValidThreshold =
      scoreThreshold !== '' &&
      Number.isFinite(numericThreshold) &&
      numericThreshold >= MIN_SCORE_THRESHOLD &&
      numericThreshold <= MAX_SCORE_THRESHOLD;
    if (!isValidThreshold) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.INVALID_THRESHOLD,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    const reportTypeLabel = SCORE_GAP_REPORT_TYPE_LABELS[reportType] || reportType;
    const schemaLabel = isCustomerReport && selectedSchema ? selectedSchema.label : '';

    // Build confirmation message parts
    const messageParts = [
      'Generate the attribute-gap report for:',
      `Manufacturer: ${selectedManufacturer.label}`,
      `Report type: ${reportTypeLabel}`,
    ];

    if (schemaLabel) {
      messageParts.push(`Schema: ${schemaLabel}`);
    }

    messageParts.push(
      `Score threshold: ${numericThreshold} — products at or above ` +
        `this stored fsa_score will be skipped.`,
      'The report runs asynchronously and may take several minutes.',
    );

    Swal.fire({
      title: 'Generate Report?',
      text: messageParts.join('\n\n'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, generate report',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        const payload = {
          global_manufacturer_id: selectedManufacturer.value,
          score_threshold: numericThreshold,
          report_type: reportType,
        };
        if (isCustomerReport) {
          payload.schema_id = selectedSchema.value;
        }
        queueReportPost(payload);
      }
    });
  }, [
    selectedManufacturer,
    scoreThreshold,
    reportType,
    selectedSchema,
    isCustomerReport,
    queueReportPost,
  ]);

  return (
    <Card className={css(styles.card)}>
      <Card.Body>
        <p className={css(styles.description)}>
          Generates an attribute-gap CSV for a single manufacturer. For every active manufacturer
          product whose stored <code>fsa_score</code> is below the threshold, the report lists the
          missing attributes needed to reach each section&apos;s B-score target. Choose{' '}
          <strong>Internal</strong> for the FSA team&apos;s long-format diagnostic view, or{' '}
          <strong>Customer-facing</strong> for a wide-format CSV using the schema&apos;s display
          names that customers understand. Once the report finishes, download it from the{' '}
          <a href="/file-exports" target="_blank" rel="noopener noreferrer">
            File Exports page
          </a>
          .
        </p>
        <hr />

        <Form>
          <Row>
            <Col md={8}>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label>Manufacturer</Form.Label>
                <SearchableScrollableSelect
                  key="mfr-score-gap-mfr-select"
                  initOption={selectedManufacturer}
                  onChange={option => setSelectedManufacturer(option)}
                  fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                  optionMapper={manufacturer => ({
                    label: manufacturer.name,
                    value: manufacturer.id,
                  })}
                  autoFocus={false}
                  placeholder="Search and select manufacturer..."
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label>Score Threshold</Form.Label>
                <Form.Control
                  type="number"
                  min={MIN_SCORE_THRESHOLD}
                  max={MAX_SCORE_THRESHOLD}
                  step="1"
                  value={scoreThreshold}
                  onChange={e => setScoreThreshold(e.target.value)}
                  placeholder={String(DEFAULT_SCORE_THRESHOLD)}
                />
                <Form.Text className="text-muted">
                  Defaults to {DEFAULT_SCORE_THRESHOLD}. Only products with a stored{' '}
                  <code>fsa_score</code> below this value (or null) are included.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label>Report Type</Form.Label>
                <Select
                  options={REPORT_TYPE_OPTIONS}
                  value={REPORT_TYPE_OPTIONS.find(opt => opt.value === reportType) || null}
                  onChange={opt => {
                    setReportType(opt?.value ?? ScoreGapReportType.INTERNAL);
                    setSelectedSchema(null);
                  }}
                  placeholder="Select report type..."
                  isClearable={false}
                />
                <Form.Text className="text-muted">
                  Internal: long-format for the FSA team. Customer-facing: wide-format using schema
                  display names.
                </Form.Text>
              </Form.Group>
            </Col>
            {isCustomerReport && (
              <Col md={8}>
                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>Schema</Form.Label>
                  <Select
                    isClearable
                    isLoading={schemasLoading}
                    options={schemas.map(schema => ({
                      label: schema.schema_name,
                      value: schema.id,
                    }))}
                    value={selectedSchema}
                    onChange={opt => setSelectedSchema(opt)}
                    placeholder="Select schema for customer display names..."
                  />
                  <Form.Text className="text-muted">
                    The schema whose categories and display names will be used as CSV column headers
                    and attribute labels in the customer report.
                  </Form.Text>
                </Form.Group>
              </Col>
            )}
          </Row>

          <Alert variant="info" className={css(styles.infoBox)}>
            <strong>Note:</strong>
            {' Products with an '}
            <code>fsa_score</code>
            {' of '}
            <code>null</code>
            {' or '}
            <code>0</code>
            {' are recomputed once before filtering. Products in the '}
            <code>Administrative</code>
            {' L0 category are skipped because no FSA score applies. The customer-facing report '}
            {'only includes attributes visible in the selected schema.'}
          </Alert>

          <div className={css(styles.actions)}>
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={!selectedManufacturer || (isCustomerReport && !selectedSchema) || queueing}
              className={css(styles.generateButton)}
            >
              {queueing ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Queuing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileExport} /> Generate Report
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: `1px solid ${lightGrayBorder}`,
  },
  description: {
    fontSize: '0.95rem',
    color: bootstrapSecondary,
    marginBottom: '1rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  infoBox: {
    marginTop: '0.5rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  generateButton: {
    minWidth: '180px',
    backgroundColor: grape,
    borderColor: grape,
  },
});

export default ManufacturerScoreGapReport;
